import { useEffect } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Grid, SelectChangeEvent, SvgIcon, Typography, useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { IconFileExport, IconSearch } from '@tabler/icons-react';
import { Button, DateTimePicker, IconButton, Select } from '@atoms';
import { useIsAreasAdmin, useShowAreaFeature, useURLparams, useUser } from '@hooks';
import { SelectAreas, SelectCustomer } from '@organisms';
import { getDatesFromTheLastMonth, isoStringToDate, toISOStringLocal } from '@utils';
import { zodResolver } from '@hookform/resolvers/zod';
import searchBoxMyDispersionsSchema, {
  DEFAULT_SCHEDULE_TYPE,
  DEFAULT_STATUS,
  SCHEDULE_TYPE_OPTIONS,
  STATUS_OPTIONS,
  DispersionStatusFilter,
  ScheduleType,
} from './SearchBoxMyDispersions.schema';
import useDownloadMyDispersions from '../data/useDownloadMyDispersions';
import styles from '../styles';

const DATE_PICKER_PROPS = {
  size: 'medium',
  dateOnly: true,
  readOnly: false,
  disablePast: false,
  calendarViews: ['year', 'month', 'day'],
  fullWidth: true,
};

const SCHEDULE_TYPE_LABELS: Record<ScheduleType, string> = {
  all: 'DISPERSIONS:SCHEDULE_TYPE_ALL',
  instant: 'DISPERSIONS:SCHEDULE_TYPE_INSTANT',
  scheduled: 'DISPERSIONS:SCHEDULE_TYPE_SCHEDULED',
};

const STATUS_LABELS: Record<DispersionStatusFilter, string> = {
  all: 'DISPERSIONS:STATUS_FILTER_ALL',
  pending: 'COMMON:PENDING',
  processing: 'COMMON:PROCESSING',
  processed: 'COMMON:COMPLETED',
  failed: 'COMMON:FAILED',
};

interface SearchBoxMyDispersionsProps {
  isLoading?: boolean;
  dataLength?: number;
}

interface SearchFormValues {
  customerId?: string | null;
  areaId?: string | null;
  fromDate?: Date;
  toDate?: Date;
  scheduleType: ScheduleType;
  status: DispersionStatusFilter;
}

/**
 * Search box filters from My dispersions
 */
const SearchBoxMyDispersions = ({
  isLoading = false,
  dataLength = 0,
}: SearchBoxMyDispersionsProps) => {
  const { t } = useTranslation();
  const { isAreasAdmin } = useIsAreasAdmin();
  const { showAreaFeature } = useShowAreaFeature();
  const { user } = useUser();
  const { params, setURLparams } = useURLparams<{
    customerId?: string;
    areaId?: string;
    fromDate?: string;
    toDate?: string;
    scheduleType?: ScheduleType;
    status?: DispersionStatusFilter;
  }>();
  const { download, isLoading: isLoadingDownload } = useDownloadMyDispersions();
  const responsive = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { fromDate, toDate } = getDatesFromTheLastMonth();
  const scheduleTypeParam = SCHEDULE_TYPE_OPTIONS.includes(params.scheduleType as ScheduleType)
    ? (params.scheduleType as ScheduleType)
    : DEFAULT_SCHEDULE_TYPE;
  const statusParam = STATUS_OPTIONS.includes(params.status as DispersionStatusFilter)
    ? (params.status as DispersionStatusFilter)
    : DEFAULT_STATUS;

  /**
   * Sync default filter values into the URL so the first request is not empty
   */
  useEffect(() => {
    const missingDefaults = !params.fromDate || !params.toDate;
    if (!missingDefaults) return;

    setURLparams({
      ...(params.customerId && { customerId: params.customerId }),
      ...(params.areaId && { areaId: params.areaId }),
      ...(params.status && params.status !== DEFAULT_STATUS && { status: params.status }),
      ...(scheduleTypeParam !== DEFAULT_SCHEDULE_TYPE && { scheduleType: scheduleTypeParam }),
      fromDate: params.fromDate || toISOStringLocal(fromDate),
      toDate: params.toDate || toISOStringLocal(toDate),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<SearchFormValues>({
    defaultValues: {
      customerId: params.customerId || null,
      areaId: params.areaId || null,
      fromDate: params.fromDate ? isoStringToDate(params.fromDate) : fromDate,
      toDate: params.toDate ? isoStringToDate(params.toDate) : toDate,
      scheduleType: scheduleTypeParam,
      status: statusParam,
    },
    resolver: zodResolver(searchBoxMyDispersionsSchema),
    mode: 'onChange',
  });

  const selectedCustomerId = useWatch({ control, name: 'customerId' });

  /**
   * Apply filters via URL params only on search
   */
  const onSubmit = (formValues: SearchFormValues) => {
    const newParams: Record<string, string> = {
      ...(formValues.customerId && { customerId: formValues.customerId }),
      ...(formValues.areaId && { areaId: formValues.areaId }),
      ...(formValues.fromDate && { fromDate: toISOStringLocal(formValues.fromDate) }),
      ...(formValues.toDate && { toDate: toISOStringLocal(formValues.toDate) }),
      ...(formValues.scheduleType &&
        formValues.scheduleType !== DEFAULT_SCHEDULE_TYPE && {
          scheduleType: formValues.scheduleType,
        }),
      ...(formValues.status &&
        formValues.status !== DEFAULT_STATUS && { status: formValues.status }),
    };
    setURLparams(newParams);
  };

  /**
   * Reset form filters and clear URL filters
   */
  const clearFilters = () => {
    reset({
      customerId: null,
      areaId: null,
      fromDate,
      toDate,
      scheduleType: DEFAULT_SCHEDULE_TYPE,
      status: DEFAULT_STATUS,
    });
    setURLparams({
      fromDate: toISOStringLocal(fromDate),
      toDate: toISOStringLocal(toDate),
    });
  };

  const disableButton = isLoading || isSubmitting;

  return (
    <Box sx={styles.searchContainer}>
      <Box sx={styles.header}>
        <Typography variant='h4'>{t('DISPERSIONS:SEARCH_MY_DISPERSIONS')}</Typography>
      </Box>
      <Box sx={styles.content(responsive)}>
        <Box sx={styles.firstLine(responsive)}>
          <Grid container spacing={2}>
            {user?.isInternal && (
              <Grid item xs={12} md={4}>
                <Controller
                  name='customerId'
                  control={control}
                  render={({ field }) => (
                    <SelectCustomer
                      value={field.value || ''}
                      onChange={(customerId) => {
                        field.onChange(customerId);
                        setValue('areaId', null);
                      }}
                      onClear={() => {
                        field.onChange(null);
                        setValue('areaId', null);
                      }}
                    />
                  )}
                />
              </Grid>
            )}
            {showAreaFeature && (
              <Grid item xs={12} md={4}>
                <Controller
                  name='areaId'
                  control={control}
                  render={({ field }) => (
                    <SelectAreas
                      value={field.value || undefined}
                      customerId={selectedCustomerId ? Number(selectedCustomerId) : undefined}
                      onChange={(_event, value) => {
                        field.onChange(value?.id || null);
                      }}
                      optionWithoutAreas={isAreasAdmin}
                      parseOption={({ id, fantasyName }) => ({ id: id, label: fantasyName })}
                      onClear={() => field.onChange(null)}
                      disabled={user?.isInternal && !selectedCustomerId}
                      placeholder='COMMON:SELECT_AREA'
                    />
                  )}
                />
              </Grid>
            )}
            <Grid item xs={12} md={4}>
              <Controller
                name='scheduleType'
                control={control}
                render={({ field }) => (
                  <Select
                    name={field.name}
                    value={field.value}
                    onChange={(event: SelectChangeEvent<string>) =>
                      field.onChange(event.target.value)
                    }
                    onBlur={field.onBlur}
                    label={t('DISPERSIONS:SCHEDULE_TYPE')}
                    items={[...SCHEDULE_TYPE_OPTIONS]}
                    parseOption={(option: ScheduleType) => ({
                      label: t(SCHEDULE_TYPE_LABELS[option]),
                      value: option,
                    })}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name='status'
                control={control}
                render={({ field }) => (
                  <Select
                    name={field.name}
                    value={field.value}
                    onChange={(event: SelectChangeEvent<string>) =>
                      field.onChange(event.target.value)
                    }
                    onBlur={field.onBlur}
                    label={t('COMMON:STATUS')}
                    items={[...STATUS_OPTIONS]}
                    parseOption={(option: DispersionStatusFilter) => ({
                      label: t(STATUS_LABELS[option]),
                      value: option,
                    })}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <Controller
                name='fromDate'
                control={control}
                render={({ field }) => (
                  <DateTimePicker
                    {...field}
                    handleChange={(date: Date) => field.onChange(date)}
                    error={Boolean(errors.fromDate)}
                    inputLabel='COMMON:FROM_DATE'
                    helperText={errors.fromDate ? String(errors.fromDate?.message) : undefined}
                    {...DATE_PICKER_PROPS}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <Controller
                name='toDate'
                control={control}
                render={({ field }) => (
                  <DateTimePicker
                    {...field}
                    handleChange={(date: Date) => field.onChange(date)}
                    error={Boolean(errors.toDate)}
                    inputLabel='COMMON:TO_DATE'
                    helperText={errors.toDate ? String(errors.toDate?.message) : undefined}
                    {...DATE_PICKER_PROPS}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
        <Box sx={styles.actionsContainer()}>
          <Button
            customStyle={styles.clearButton}
            text={t('COMMON:CLEAR_FILTER')}
            color='primary'
            variant='link'
            onClick={clearFilters}
          />
          <IconButton
            aria-label={t('COMMON:EXPORT')}
            tooltip='COMMON:EXPORT'
            onClick={() => download(params as Record<string, string>)}
            IconComponent={IconFileExport}
            isLoading={isLoadingDownload}
            disabled={!dataLength || isLoading}
          />
          <Button
            customStyle={styles.searchButton}
            startIcon={
              <SvgIcon>
                <IconSearch />
              </SvgIcon>
            }
            variant='contained'
            color='primary'
            type='button'
            disabled={disableButton}
            isLoading={isLoading}
            onClick={handleSubmit(onSubmit)}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default SearchBoxMyDispersions;
